import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Dashboard,
  Description,
  WorkHistory,
  History,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";

const EsignSidebar = ({ forceCollapsed = false }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    } else if (isSmallScreen || isMobile) {
      setCollapsed(true);
    } else if (isTablet) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [forceCollapsed, isSmallScreen, isMobile, isTablet]);

  const handleDrawerToggle = () => {
    if (!isMobile && !forceCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const drawerWidth = collapsed ? 72 : 280;

  const menuItems = [
    { title: "Dashboard", icon: <Dashboard />, path: "/EsignDashboard" },
    { title: "Documents", icon: <Description />, path: "/EsignDocument" },
    { title: "Signatures", icon: <Description />, path: "/EsignSignature" },
    {
      title: "Workflow Management",
      icon: <WorkHistory />,
      path: "/EsignWorkplaceManagement",
    },
    { title: "History", icon: <History />, path: "/EsignHistory" },
  ];

  const renderMenuItem = (item) => (
    <ListItemButton
      key={item.title}
      component={Link}
      to={item.path}
      selected={location.pathname === item.path}
      sx={{
        minHeight: 48,
        px: collapsed ? 1.5 : 2.5,
        margin: "4px 16px",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "#FFF6E9",
          transform: "translateX(4px)",
          "& .MuiListItemText-primary": {
            color: "#EB5A3C",
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: "#EB5A3C",
          },
        },
        "&.Mui-selected": {
          backgroundColor: "#FFF6E9",
          "& .MuiListItemText-primary": {
            color: "#EB5A3C",
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: "#EB5A3C",
          },
          "&:hover": {
            backgroundColor: "#FFF6E9",
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 3,
          justifyContent: "center",
          color: location.pathname === item.path ? "#EB5A3C" : theme.palette.primary.main,
        }}
      >
        <Tooltip
          title={item.title}
          placement="right"
          disableHoverListener={!collapsed}
        >
          {item.icon}
        </Tooltip>
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.title}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              fontWeight: 500,
              color: location.pathname === item.path ? "#EB5A3C" : "inherit",
            },
          }}
        />
      )}
    </ListItemButton>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#333333",
                fontSize: "1.2rem",
              }}
            >
              <Link
                to="/Dashboard"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "flex",
                  alignItems: "center",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    color: "black",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                  }}
                >
                  E-Sign
                </Typography>
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    color: "#D32F2F",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    ml: 1,
                  }}
                >
                  portal
                </Typography>
              </Box>
            </Typography>
          </Box>
        )}
        {!isMobile && !forceCollapsed && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <List>{menuItems.map(renderMenuItem)}</List>
      </Box>

      {/* Footer Logo */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: "100%",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "fixed",
          top: 64,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default EsignSidebar;
